import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { useEmployeeById } from "../../../store/selectors/employeesSelectors";
import { deleteEmployeePasswordThunk, bookTransactionForEmployeeThunk, fetchEmployeeByIdThunk } from "../../../store/thunk/employeesThunk";
import {
    Container,
    EmployeeCard,
    InfoRow,
    Label,
    Value,
    GroupsWrapper,
    WidgetRow,
    TopRow,
    InfoBlock,
    InfoGrid,
    GroupsColumn,
    InfoColumnGrid,
    NameBlock,
    NameFirst,
    NameLast,
    NameRow,
} from "./SingleEmployee.styles";
import { EmployeeUserType } from "../../../types/employeeType";
import { useGroupsData } from "../../../store/selectors/groupSelectors";
import { useTranslation } from "react-i18next";
import ModuleListWidget from "./ModuleListWidget";
import ActionModuleItem from "./ActionModuleItem";
import ServiceModuleItem from "./ServiceModuleItem";
import ShopModuleItem from "./ShopModuleItem";
import WalletWidget from "./WalletWidget";
import EmployeeQuotasWidget from "./EmployeeQuotasWidget";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../../types/permissionType";
import { TransactionIcon } from "../../../assets/icons/Icons";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import { GeneralButton } from "../../../common/ui/GeneralButton";

interface EmployeeGroupsProps {
    groupIds?: number[];
    groups?: { id: number; name?: string }[];
}

const EmployeeGroups: FC<EmployeeGroupsProps> = ({ groupIds = [], groups = [] }) => {
    const groupList = useGroupsData();
    const groupNames = useMemo(() => {
        const effectiveGroupIds =
            groupIds.length > 0 ? groupIds : (groups?.map((g) => g.id) ?? []);
        if (!effectiveGroupIds || effectiveGroupIds.length === 0) return [];
        return effectiveGroupIds.map(
            (id) => groupList.find((g) => g.id === id)?.name
        );
    }, [groupIds, groups, groupList]);

    if (groupNames.length === 0) return <Typography variant="caption">-</Typography>;

    return (
        <GroupsWrapper>
            {groupNames.map((name, idx) => (
                <span key={idx}>
                    {name}
                </span>
            ))}
        </GroupsWrapper>
    );
};

const SingleEmployee = () => {
    const { employeeId } = useParams<{ employeeId: string }>();
    const dispatch = useAppDispatch();
    const employee = useEmployeeById(employeeId ?? "") as EmployeeUserType | undefined;
    const { t } = useTranslation();
    const { userHasPermission } = usePermissionChecker();
    const hasEditPermission = userHasPermission(PermissionEntityType.Employees, "Create");

    const [confirmResetTitleOpen, setConfirmResetTitleOpen] = useState(false);
    const handleOpenConfirm = () => setConfirmResetTitleOpen(true);
    const handleCloseConfirm = () => setConfirmResetTitleOpen(false);
    const handleResetPassword = async () => {
        if (!employeeId) return;
        await dispatch(deleteEmployeePasswordThunk(employeeId));
        setConfirmResetTitleOpen(false);
    };

    const [confirmTransactionOpen, setConfirmTransactionOpen] = useState(false);
    const handleConfirmTransactionOpen = () => setConfirmTransactionOpen(true);
    const handleConfirmTransactionClose = () => setConfirmTransactionOpen(false);
    const handleConfirmTransaction = async () => {
        if (!employeeId) return;
        await dispatch(bookTransactionForEmployeeThunk({ employeeId }));
        setConfirmTransactionOpen(false);
    };

    useEffect(() => {
        if (employeeId && !employee) {
            dispatch(fetchEmployeeByIdThunk(employeeId));
        }
    }, [dispatch, employeeId, employee]);

    if (!employee) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container>
            <EmployeeCard>
                <TopRow>
                    <NameBlock>
                        <NameRow>
                            <NameFirst>{employee.firstName}</NameFirst>
                            <NameLast>{employee.lastName}</NameLast>
                        </NameRow>

                        {hasEditPermission && (
                            <GeneralButton
                                appVariant="danger"
                                sx={{ mt: 2, alignSelf: "flex-start" }}
                                onClick={handleOpenConfirm}
                            >
                                {t("employeesPage.singleEmployee.resetPassword")}
                            </GeneralButton>
                        )}

                        <GeneralButton
                            sx={{
                                mt: 1.5,
                                alignSelf: "flex-start",
                                display: "flex",
                                gap: 1,
                                minWidth: 160,
                            }}
                            onClick={handleConfirmTransactionOpen}
                        >
                            <TransactionIcon colorHex="#fff" />
                            {t("buttons.bookTransaction")}
                        </GeneralButton>
                    </NameBlock>

                    <InfoBlock>
                        <InfoGrid>
                            <InfoColumnGrid>
                                <InfoRow>
                                    <Label sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{t("tableColumnHeaders.position")}</Label>
                                    <Value>{employee.position || "—"}</Value>
                                </InfoRow>
                                <InfoRow>
                                    <Label sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{t("tableColumnHeaders.employeeNo")}</Label>
                                    <Value>{employee.employeeNumber || "—"}</Value>
                                </InfoRow>
                            </InfoColumnGrid>

                            <InfoColumnGrid>
                                <InfoRow>
                                    <Label sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{t("tableColumnHeaders.birthDate")}</Label>
                                    <Value>{employee.birthDate ? dayjs(employee.birthDate).format("DD.MM.YYYY") : "—"}</Value>
                                </InfoRow>
                                <InfoRow>
                                    <Label sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{t("tableColumnHeaders.employmentDate")}</Label>
                                    <Value>{employee.employmentDate ? dayjs(employee.employmentDate).format("DD.MM.YYYY") : "—"}</Value>
                                </InfoRow>
                            </InfoColumnGrid>

                            <InfoColumnGrid>
                                <InfoRow>
                                    <Label sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{t("tableColumnHeaders.subCompany")}</Label>
                                    <Value>{employee.subCompanyName || "—"}</Value>
                                </InfoRow>
                                <InfoRow>
                                    <Label sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{t("tableColumnHeaders.status")}</Label>
                                    <Value>{employee.status || "—"}</Value>
                                </InfoRow>
                            </InfoColumnGrid>

                            <GroupsColumn>
                                <Label sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{t("tableColumnHeaders.groups")}</Label>
                                <EmployeeGroups groupIds={employee.groupIds} />
                            </GroupsColumn>
                        </InfoGrid>
                    </InfoBlock>

                    <ConfirmDialog
                        open={confirmResetTitleOpen}
                        title={t("employeesPage.singleEmployee.confirmResetTitle")}
                        message={t("employeesPage.singleEmployee.confirmResetText", {
                            name: `${employee.firstName} ${employee.lastName}`,
                        })}
                        onClose={handleCloseConfirm}
                        onConfirm={handleResetPassword}
                    />

                    <ConfirmDialog
                        open={confirmTransactionOpen}
                        title={t("buttons.bookTransaction")}
                        message={t("dialogModals.confirmTransactionEmployee")}
                        onClose={handleConfirmTransactionClose}
                        onConfirm={handleConfirmTransaction}
                    />
                </TopRow>
                <WidgetRow>
                    <Box sx={{ flex: 1, minWidth: "350px" }}>
                        <ModuleListWidget
                            title={t("actionModulesPage.actionModules.title")}
                            items={employee.actionModules ?? []}
                            emptyLabel={t("employeesPage.singleEmployee.noActionModules")}
                            renderItem={(m: any) => <ActionModuleItem module={m} />}
                        />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: "350px" }}>
                        <ModuleListWidget
                            title={t("serviceModulesPage.serviceModules")}
                            items={employee.serviceModules ?? []}
                            emptyLabel={t("employeesPage.singleEmployee.noServiceModules")}
                            renderItem={(m: any) => <ServiceModuleItem module={m} />}
                        />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: "350px" }}>
                        <ModuleListWidget
                            title={t("shopModulesPage.shopModules")}
                            items={employee.shopModules ?? []}
                            emptyLabel={t("employeesPage.singleEmployee.noShopModules")}
                            renderItem={(m: any) => <ShopModuleItem module={m} />}
                        />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: "350px" }}>
                        <WalletWidget walletId={employee.walletId} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: "350px" }}>
                        <EmployeeQuotasWidget employee={employee} />
                    </Box>
                </WidgetRow>
            </EmployeeCard>
        </Container>
    );
};

export default SingleEmployee;
